import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Users, BookOpen, Layers, Calendar, GraduationCap, ArrowUpRight, CreditCard, Megaphone, Trash2, Send, ChevronRight, Package } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [newAnn, setNewAnn] = useState("");

  const loadAnn = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements");
      setAnnouncements(data);
    } catch { setAnnouncements([]); }
  }, []);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));
    loadAnn();
  }, [loadAnn]);

  const postAnnouncement = async () => {
    if (!newAnn.trim()) return;
    try {
      await api.post("/announcements", { message: newAnn.trim() });
      setNewAnn("");
      toast.success("Announcement posted");
      loadAnn();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteAnn = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      loadAnn();
    } catch { toast.error("Failed to delete"); }
  };

  const cards = [
    { label: "Students", value: stats?.total_students ?? "...", icon: GraduationCap, color: "#5C3A2E" },
    { label: "Courses", value: stats?.total_courses ?? "...", icon: BookOpen, link: "/admin/courses", color: "#C6A969" },
    { label: "Modules", value: stats?.total_modules ?? "...", icon: Layers, color: "#5C3A2E" },
    { label: "Groups", value: stats?.total_groups ?? "...", icon: Users, color: "#C6A969" },
    { label: "Sessions", value: stats?.total_sessions ?? "...", icon: Calendar, link: "/admin/sessions", color: "#5C3A2E" },
    { label: "Payments", value: stats?.total_payments ?? "...", icon: CreditCard, link: "/admin/payments", color: "#C6A969" },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard" className="space-y-8 animate-fade-in-up">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">
            Studio Overview
          </h1>
          <p className="text-[#2B2B2B]/50 mt-1 text-sm font-body">Everything at a glance. Click a course to dive deeper.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {cards.map((card, i) => {
            const Icon = card.icon;
            const inner = (
              <div
                key={card.label}
                data-testid={`stat-card-${card.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 studio-card animate-fade-in-up animate-stagger-${i + 1}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${card.color}15` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color: card.color }} />
                  </div>
                  {card.link && <ArrowUpRight className="w-3.5 h-3.5 text-[#2B2B2B]/20" />}
                </div>
                <p className="text-2xl font-semibold text-[#2B2B2B] font-body">{card.value}</p>
                <p className="text-xs text-[#2B2B2B]/50 mt-0.5">{card.label}</p>
              </div>
            );
            return card.link ? <Link key={card.label} to={card.link}>{inner}</Link> : <div key={card.label}>{inner}</div>;
          })}
        </div>

        {/* Courses quick access */}
        {courses.length > 0 && (
          <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#C6A969]" />
                <h2 className="font-heading text-lg text-[#5C3A2E] font-semibold">Your Courses</h2>
              </div>
              <Link to="/admin/courses" className="text-xs text-[#C6A969] hover:text-[#5C3A2E] font-medium flex items-center gap-0.5 transition-colors">
                View all <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="grid gap-2">
              {courses.map((c) => (
                <button
                  key={c.id}
                  data-testid={`dashboard-course-${c.id}`}
                  onClick={() => navigate(`/admin/courses/${c.id}`)}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-xl hover:bg-[#EADBC8]/20 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#5C3A2E]/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-4 h-4 text-[#5C3A2E]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#2B2B2B] truncate">{c.title}</p>
                    {c.subtitle && <p className="text-[10px] text-[#2B2B2B]/40 truncate">{c.subtitle}</p>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#2B2B2B]/20 group-hover:text-[#C6A969] transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Announcements */}
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone className="w-5 h-5 text-[#C6A969]" />
            <h2 className="font-heading text-lg text-[#5C3A2E] font-semibold">Announcements</h2>
          </div>
          <div className="flex gap-2 mb-4">
            <Input
              data-testid="announcement-input"
              value={newAnn}
              onChange={(e) => setNewAnn(e.target.value)}
              placeholder="Post a message to all students..."
              className="rounded-xl border-[#EADBC8] flex-1"
              onKeyDown={(e) => e.key === "Enter" && postAnnouncement()}
            />
            <Button data-testid="post-announcement-button" onClick={postAnnouncement} disabled={!newAnn.trim()} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          {announcements.length > 0 ? (
            <div className="space-y-2">
              {announcements.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#F7F3EF]">
                  <p className="text-sm text-[#2B2B2B] flex-1">{a.message}</p>
                  <span className="text-xs text-[#2B2B2B]/30 flex-shrink-0">{new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                  <Button variant="ghost" size="sm" onClick={() => deleteAnn(a.id)} className="text-red-400 hover:bg-red-50 rounded-full h-7 w-7 p-0 flex-shrink-0">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#2B2B2B]/40">No announcements. Post one above to notify all students.</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
